import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { formatDuration, formatTimecode, parseTimecode } from '../../utils/timecode';
import { formatDate } from '../../utils/format';
import StatusChip from '../ui/StatusChip';
import { WorkspaceTask, taskEnd, taskStart, taskStatusKey } from './taskMeta';

export interface TaskFormValues {
  title: string;
  description: string;
  type: string;
  priority: string;
  estimatedMinutes: number;
  actualMinutes: number | null;
  editorNotes: string;
  start: number;
  end: number;
}

interface TaskFormDialogProps {
  open: boolean;
  /** null creates a new task. */
  task: WorkspaceTask | null;
  fps: number;
  /** Where a new task starts (the playhead). */
  defaultStart: number;
  saving: boolean;
  disabled: boolean;
  onClose: () => void;
  onSave: (values: TaskFormValues) => void;
}

const TYPES: Array<[string, string]> = [
  ['MISC', 'General'],
  ['ANNOTATION', 'Feedback'],
  ['ASSET_INTEGRATION', 'Asset integration'],
  ['EFFECT_APPLICATION', 'Effects'],
  ['TRANSITION', 'Transition'],
  ['COLOR_CORRECTION', 'Colour correction'],
  ['AUDIO_SYNC', 'Audio'],
];

/** Create or edit a task: text, type, priority, timing (timecode) and time tracking. */
const TaskFormDialog: React.FC<TaskFormDialogProps> = ({ open, task, fps, defaultStart, saving, disabled, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('MISC');
  const [priority, setPriority] = useState('MEDIUM');
  const [estimated, setEstimated] = useState('30');
  const [actual, setActual] = useState('');
  const [editorNotes, setEditorNotes] = useState('');
  const [startText, setStartText] = useState('');
  const [endText, setEndText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const start = task ? taskStart(task) ?? 0 : Math.max(0, defaultStart);
    const end = task ? taskEnd(task) ?? start + 5 : start + 5;
    setTitle(task?.title || '');
    setDescription(task?.description || '');
    setType(task?.type || 'MISC');
    setPriority(task?.priority || 'MEDIUM');
    setEstimated(String(task?.estimatedMinutes ?? 30));
    setActual(task?.actualMinutes ? String(task.actualMinutes) : '');
    setEditorNotes(task?.editorNotes || '');
    setStartText(formatTimecode(start, fps));
    setEndText(formatTimecode(end, fps));
    setError(null);
    // Reset only when the dialog opens for a (different) task.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task?.id]);

  const start = parseTimecode(startText, fps);
  const end = parseTimecode(endText, fps);

  const submit = () => {
    if (!title.trim()) {
      setError('Give the task a title.');
      return;
    }
    if (start === null || end === null || end <= start) {
      setError('Enter a start and an end (after the start) as timecode, m:ss or seconds.');
      return;
    }
    const estimatedMinutes = Math.max(0, parseInt(estimated, 10) || 0);
    const actualMinutes = actual.trim() === '' ? null : Math.max(0, parseInt(actual, 10) || 0);
    onSave({
      title: title.trim(),
      description: description.trim(),
      type,
      priority,
      estimatedMinutes,
      actualMinutes,
      editorNotes: editorNotes.trim(),
      start,
      end,
    });
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth aria-labelledby="task-form-title">
      <DialogTitle id="task-form-title" sx={{ fontWeight: 700 }}>{task ? 'Edit task' : 'Create task'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required autoFocus />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <FormControl fullWidth>
              <InputLabel id="task-type-label">Type</InputLabel>
              <Select labelId="task-type-label" label="Type" value={type} onChange={(e) => setType(String(e.target.value))}>
                {TYPES.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="task-priority-label">Priority</InputLabel>
              <Select labelId="task-priority-label" label="Priority" value={priority} onChange={(e) => setPriority(String(e.target.value))}>
                <MenuItem value="LOW">Low</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
            <TextField
              label="Start"
              value={startText}
              onChange={(e) => setStartText(e.target.value)}
              error={start === null}
              helperText={start === null ? 'HH:MM:SS:FF, m:ss or seconds' : `${start.toFixed(2)}s`}
              inputProps={{ style: { fontVariantNumeric: 'tabular-nums' } }}
              fullWidth
            />
            <TextField
              label="End"
              value={endText}
              onChange={(e) => setEndText(e.target.value)}
              error={end === null || (start !== null && end <= start)}
              helperText={end === null ? 'HH:MM:SS:FF, m:ss or seconds' : start !== null && end > start ? `Duration ${formatDuration(end - start)}` : 'Must be after the start'}
              inputProps={{ style: { fontVariantNumeric: 'tabular-nums' } }}
              fullWidth
            />
            <TextField
              label="Estimated minutes"
              type="number"
              value={estimated}
              onChange={(e) => setEstimated(e.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
            />
            {task && (
              <TextField
                label="Actual minutes"
                type="number"
                value={actual}
                onChange={(e) => setActual(e.target.value)}
                inputProps={{ min: 0 }}
                helperText="Time you spent"
                fullWidth
              />
            )}
          </Box>
          <TextField
            label="Your notes"
            value={editorNotes}
            onChange={(e) => setEditorNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            helperText="Your own notes on how you handled this task"
          />
          {task && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', color: 'text.secondary', fontSize: '0.875rem' }}>
              <StatusChip kind="task" status={taskStatusKey(task.status)} />
              {task.createdAt && formatDate(task.createdAt) ? <span>Created {formatDate(task.createdAt)}</span> : null}
              {task.completedAt && formatDate(task.completedAt) ? <span>· Done {formatDate(task.completedAt)}</span> : null}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving || disabled}>
          {saving ? 'Saving…' : task ? 'Save changes' : 'Create task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFormDialog;
